import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalculateIcon from "@mui/icons-material/Calculate";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import FontDownloadIcon from "@mui/icons-material/FontDownload";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import NumbersIcon from "@mui/icons-material/Numbers";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import DensityLargeIcon from "@mui/icons-material/DensityLarge";
import DrawIcon from "@mui/icons-material/Draw";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertPageBreakIcon from "@mui/icons-material/InsertPageBreak";
import { Box, List, Typography } from "@mui/material";
import React from "react";
import { Droppable } from "react-beautiful-dnd";
import { AppReportTaskTypeTitlePageLayout, AppReportTaskTypeTitlePageType, TaskDataRowDataType, TaskDataRowTotalType } from "ui/Scripts/source/models/enums";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { getRenderItem, FormDesignerComponentPickerItem, IFormDesignerComponentPickerItemProps, TaskFormDesignerComponentType, TaskFormDesignerSectionType } from "./FormDesignerComponentPickerItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { IdTitle } from "ui/Scripts/source/models/common/idTitle";
import { Base } from "ui/Scripts/source/framework/base";
import { IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { SectionIcon, SectionType } from "source/components/framework/Icon/SectionIcon";
import { defaultExpression } from "../../FormDesignerTypes";

const baseTaskType = {
    class: "",
    dataType: 0,
    decimals: 0,
    defaultNbr: null,
    defaultSortField: false,
    defaultSortOrderAsc: false,
    defaultStr: "",
    defaultExpression: "",
    expression: "",
    expressionFormat: "",
    expressionIndex: 0,
    field: "",
    idNodeOptions: [],
    idOptions: null,
    maxLength: null,
    maximum: null,
    minimum: null,
    options: [],
    objectValue: null,
    placeholder: "",
    readOnly: false,
    readOnlyExpression: "",
    clearExpression: "",
    required: false,
    title: "Tila",
    unitTitle: "",
    useInFilter: false,
    lineTotalType: TaskDataRowTotalType.None,
    lineTotalExpression: "",
    selectOptionsSubSectionValuesByTitle: false,
};

type ComponentDefaultProp = {
    defaultProps: Partial<TaskTypeCol>;
    defaultValueStr?: string;
}

type ComponentDefaultValue = {
    // eslint-disable-next-line no-unused-vars
    [key in TaskFormDesignerComponentType]: ComponentDefaultProp;
}

export const componentDefaultValues: ComponentDefaultValue = {
    title1: {
        defaultProps: {
            dataType: TaskDataRowDataType.Label,
            class: "col-md-12 col-12 fontBold eLabel",
            title: Translations.FormTitle1
        }
    },
    title2: {
        defaultProps: {
            dataType: TaskDataRowDataType.Label,
            class: "col-md-12 col-12 fontBold lLabel",
            title: Translations.FormTitle2
        }
    },
    title3: {
        defaultProps: {
            dataType: TaskDataRowDataType.Label,
            class: "col-md-12 col-12 fontBold mLabel",
            title: Translations.FormTitle3
        }
    },
    title4: {
        defaultProps: {
            dataType: TaskDataRowDataType.Label,
            class: "col-md-12 col-12 fontBold sLabel",
            title: Translations.FormTitle4
        }
    },
    title5: {
        defaultProps: {
            dataType: TaskDataRowDataType.Label,
            class: "col-md-12 col-12 fontNormal",
            title: Translations.FormNormalText
        }
    },
    textInput: {
        defaultProps: {
            dataType: TaskDataRowDataType.String,
            class: "col-md-6 col-6",
            maxLength: 100,
            title: Translations.FormTextInput
        }
    },
    textArea: {
        defaultProps: {
            dataType: TaskDataRowDataType.String,
            class: "col-md-12 col-12 sTextArea",
            maxLength: 1000,
            title: Translations.FormTextArea
        }
    },
    date: {
        defaultProps: {
            dataType: TaskDataRowDataType.Date,
            class: "col-md-6 col-6 ra",
            title: Translations.FormDate
        }
    },
    time: {
        defaultProps: {
            dataType: TaskDataRowDataType.Time,
            class: "col-md-6 col-6 ra",
            title: Translations.FormTime
        }
    },
    timeSpan: {
        defaultProps: {
            dataType: TaskDataRowDataType.TimeSpan,
            class: "col-md-6 col-6 ra",
            title: Translations.FormTimeSpan
        }
    },
    // dateTime: {
    //     defaultProps: {
    //         dataType: TaskDataRowDataType.DateTime,
    //         class: "col-md-3 col-6",
    //     }
    // },
    checkbox: {
        defaultProps: {
            dataType: TaskDataRowDataType.Boolean,
            class: "col-md-6 col-6",
            title: Translations.FormCheckbox
        }
    },
    radio: {
        defaultProps: {
            dataType: TaskDataRowDataType.String,
            class: "col-md-6 col-6 radio",
            idOptions: [IdTitle.createIdTitleSimple(Translations.FormOption + " 1")],
            title: Translations.FormRadio
        }
    },
    dropdown: {
        defaultProps: {
            dataType: TaskDataRowDataType.String,
            class: "col-md-6 col-6",
            idOptions: [IdTitle.createIdTitleSimple(Translations.FormOption + " 1")],
            title: Translations.FormDropdown
        }
    },
    decimal: {
        defaultProps: {
            dataType: TaskDataRowDataType.Number,
            class: "col-md-6 col-6 ra",
            title: Translations.FormDecimalNumber,
            decimals: 2
        },
    },
    integer: {
        defaultProps: {
            dataType: TaskDataRowDataType.Integer,
            class: "col-md-6 col-6 ra",
            title: Translations.FormIntegerNumber
        },
    },
    numberCalculated: {
        defaultProps: {
            dataType: TaskDataRowDataType.Number,
            class: "col-md-6 col-6 ra",
            title: Translations.FormCalculatedDecimalNumber,
            readOnly: true,
            expression: defaultExpression
        },
    },
    integerCalculated: {
        defaultProps: {
            dataType: TaskDataRowDataType.Integer,
            class: "col-md-6 col-6 ra",
            title: Translations.FormCalculatedIntegerNumber,
            readOnly: true,
            expression: defaultExpression,
            //expressionFormat: "minutesToDateStr(fieldValue)"
        },
    },
    // attachment: {
    //     defaultProps: {
    //         dataType: TaskDataRowDataType.Attachment,
    //         class: "col-md-12 col-12",
    //     }
    // },
    lineBreak: {
        defaultProps: {
            dataType: TaskDataRowDataType.LineBreak,
            class: "col-md-12 col-12",
        }
    },
    emptyArea: {
        defaultProps: {
            dataType: TaskDataRowDataType.Label,
            class: "col-md-6 col-6 fontNormal emptyLabel"
        }
    },
    // pageBreak: {
    //     defaultProps: {
    //         dataType: TaskDataRowDataType.PageBreak,
    //         class: "col-md-12 col-12",
    //     }
    // },
    signature: {
        defaultProps: {
            dataType: TaskDataRowDataType.Signature,
            class: "col-md-6 col-6 signature1",
            required: true,
        }
    },
    coverPage: {
        defaultProps: {
            dataType: TaskDataRowDataType.TitlePage,
            class: "col-md-12 col-12",
            objectValue: {
                type: AppReportTaskTypeTitlePageType.CoverPage,
                layout: AppReportTaskTypeTitlePageLayout.Layout1,
                name: "",
                image: ""
            }
        }
    },
    titlePage: {
        defaultProps: {
            dataType: TaskDataRowDataType.TitlePage,
            class: "col-md-12 col-12",
            objectValue: {
                type: AppReportTaskTypeTitlePageType.TitlePage,
                layout: AppReportTaskTypeTitlePageLayout.Layout1,
                name: "",
                image: ""
            }
        }
    },
    pageBreak: {
        defaultProps: {
            dataType: TaskDataRowDataType.PageBreak,
            class: "col-md-12 col-12",
        }
    },
    richText: {
        defaultProps: {
            dataType: TaskDataRowDataType.RichText,
            class: "col-md-12 col-12",
        }
    },
    documentReference: {
        defaultProps: {
            dataType: TaskDataRowDataType.DocumentReference,
            class: "col-md-12 col-12",
        }
    },
    eventReference: {
        defaultProps: {
            dataType: TaskDataRowDataType.EventReference,
            class: "col-md-12 col-12",
        }
    },
    taskReference: {
        defaultProps: {
            dataType: TaskDataRowDataType.TaskReference,
            class: "col-md-12 col-12",
        }
    },
    recordReference: {
        defaultProps: {
            dataType: TaskDataRowDataType.RecordReference,
            class: "col-md-12 col-12",
        }
    }
};

export const getComponentNameForNewIntance = (names: string[], componentType: TaskFormDesignerComponentType) => {
    const nameBase = TaskDataRowDataType[componentDefaultValues[componentType].defaultProps.dataType];
    let index = 1;
    while (names.find(item => item === nameBase + index) !== undefined) {
        index += 1;
    }
    return nameBase + index;
};

export const getComponentTitleForNewIntance = (titles: string[], componentType: TaskFormDesignerComponentType) => {
    if (componentType === TaskFormDesignerComponentType.EmptyArea) return "";
    const defaultValues = componentDefaultValues[componentType];
    const titleBase = defaultValues.defaultProps.title ?? TaskDataRowDataType[defaultValues.defaultProps.dataType];
    let index = 1;
    while (titles.find(item => item === titleBase + " " + index) !== undefined) {
        index += 1;
    }
    return titleBase + " " + index;
};

export const getComponentDefaultValueForNewIntance = (componentType: TaskFormDesignerComponentType): string => {
    const defaultValues = componentDefaultValues[componentType];
    return defaultValues.defaultValueStr ?? "";
};

export const getTaskTypeCol = (componentType: TaskFormDesignerComponentType): TaskTypeCol => {
    const item = { ...baseTaskType, ...componentDefaultValues[componentType].defaultProps };
    return new TaskTypeCol({
        ...item,
        isSaveDataType: () => false,
        isSavedTaskDataField: () => false,
        canBeFiltered: () => false,
    });
};

export const addComponentToTaskTypeCols = (typeCols: ITaskTypeCol[], componentType: TaskFormDesignerComponentType, index: number, copyTaskTypeCol: ITaskTypeCol = null): { typeCol: ITaskTypeCol, typeCols: ITaskTypeCol[], field: string } => {
    const result = Array.from(typeCols).map(item => item);
    const item = getTaskTypeCol(componentType);
    const name = getComponentNameForNewIntance(typeCols.map(item => item.field), componentType);
    item.field = name;
    item.title = getComponentTitleForNewIntance(typeCols.map(item => item.title), componentType);
    item.defaultStr = getComponentDefaultValueForNewIntance(componentType);
    if (copyTaskTypeCol) {
        item.dataType = copyTaskTypeCol.dataType;
        item.decimals = copyTaskTypeCol.decimals;
        item.defaultStr = copyTaskTypeCol.defaultStr;
        item.defaultNbr = copyTaskTypeCol.defaultNbr;
        item.defaultExpression = copyTaskTypeCol.defaultExpression;
        item.unitTitle = copyTaskTypeCol.unitTitle;
        item.readOnly = copyTaskTypeCol.readOnly;
        item.readOnlyExpression = copyTaskTypeCol.readOnlyExpression;
        item.required = copyTaskTypeCol.required;
        item.useInFilter = false;
        item.minimum = copyTaskTypeCol.minimum;
        item.maximum = copyTaskTypeCol.maximum;
        item.maxLength = copyTaskTypeCol.maxLength;
        item.options = copyTaskTypeCol.options;
        item.idOptions = Base.getTypedArray(copyTaskTypeCol.idOptions, IdTitle);
        item.idNodeOptions = Base.getTypedArray(copyTaskTypeCol.idNodeOptions, IdTitleNode);
        item.objectValue = copyTaskTypeCol.objectValue ? structuredClone(copyTaskTypeCol.objectValue) : null;
        item.class = copyTaskTypeCol.class;
        item.expression = copyTaskTypeCol.expression;
        item.expressionFormat = copyTaskTypeCol.expressionFormat;
        item.expressionIndex = copyTaskTypeCol.expressionIndex;
        item.defaultSortField = copyTaskTypeCol.defaultSortField;
        item.defaultSortOrderAsc = copyTaskTypeCol.defaultSortOrderAsc;
        item.placeholder = copyTaskTypeCol.placeholder;
    }
    // DO NOT SET PLACEHOLDER AUTOMATICALLY
    // if (componentType === TaskFormDesignerComponentType.TextInput || componentType === TaskFormDesignerComponentType.TextArea ||
    //     componentType === TaskFormDesignerComponentType.Decimal || componentType === TaskFormDesignerComponentType.Integer) {
    //     item.placeholder = item.title;
    // }
    result.splice(index, 0, item);
    return {
        typeCol: item,
        typeCols: result,
        field: name
    };
};

const taskTypePickerItems: IFormDesignerComponentPickerItemProps[] = [
    {
        componentType: TaskFormDesignerComponentType.Title1,
        title: Translations.FormTitle1,
        icon: <TitleOutlinedIcon />,
        classes: "eLabel",
        index: 0,
        section: TaskFormDesignerSectionType.Title
    },
    {
        componentType: TaskFormDesignerComponentType.Title2,
        title: Translations.FormTitle2,
        icon: <TitleOutlinedIcon />,
        classes: "lLabel",
        index: 1,
        section: TaskFormDesignerSectionType.Title
    },
    {
        componentType: TaskFormDesignerComponentType.Title3,
        title: Translations.FormTitle3,
        icon: <TitleOutlinedIcon />,
        classes: "mLabel",
        index: 2,
        section: TaskFormDesignerSectionType.Title
    },
    {
        componentType: TaskFormDesignerComponentType.Title4,
        title: Translations.FormTitle4,
        icon: <TitleOutlinedIcon />,
        classes: "sLabel",
        index: 3,
        section: TaskFormDesignerSectionType.Title
    },
    {
        componentType: TaskFormDesignerComponentType.NormalText,
        title: Translations.FormNormalText,
        icon: <TitleOutlinedIcon />,
        classes: "fontNormal",
        index: 4,
        section: TaskFormDesignerSectionType.Title
    },
    {
        componentType: TaskFormDesignerComponentType.TextInput,
        title: Translations.FormTextInput,
        icon: <FontDownloadIcon />,
        index: 5,
        section: TaskFormDesignerSectionType.TextInput
    },
    {
        componentType: TaskFormDesignerComponentType.TextArea,
        title: Translations.FormTextArea,
        icon: <FontDownloadIcon />,
        index: 6,
        section: TaskFormDesignerSectionType.TextInput
    },
    {
        componentType: TaskFormDesignerComponentType.Date,
        title: Translations.FormDate,
        icon: <InsertInvitationIcon />,
        index: 7,
        section: TaskFormDesignerSectionType.DateAndTime
    },
    {
        componentType: TaskFormDesignerComponentType.Time,
        title: Translations.FormTime,
        icon: <AccessTimeIcon />,
        index: 8,
        section: TaskFormDesignerSectionType.DateAndTime
    },
    {
        componentType: TaskFormDesignerComponentType.TimeSpan,
        title: Translations.FormTimeSpan,
        icon: <TimelapseIcon />,
        index: 9,
        section: TaskFormDesignerSectionType.DateAndTime
    },
    {
        componentType: TaskFormDesignerComponentType.Checkbox,
        title: Translations.FormCheckbox,
        icon: <CheckBoxIcon />,
        index: 10,
        section: TaskFormDesignerSectionType.Boolean
    },
    {
        componentType: TaskFormDesignerComponentType.Dropdown,
        title: Translations.FormDropdown,
        icon: <FormatListBulletedIcon />,
        index: 11,
        section: TaskFormDesignerSectionType.Selection
    },
    {
        componentType: TaskFormDesignerComponentType.Radio,
        title: Translations.FormRadio,
        icon: <RadioButtonCheckedIcon />,
        index: 12,
        section: TaskFormDesignerSectionType.Selection
    },
    {
        componentType: TaskFormDesignerComponentType.Decimal,
        title: Translations.FormDecimalNumber,
        icon: <NumbersIcon />,
        index: 13,
        section: TaskFormDesignerSectionType.Number
    },
    {
        componentType: TaskFormDesignerComponentType.Integer,
        title: Translations.FormIntegerNumber,
        icon: <NumbersIcon />,
        index: 14,
        section: TaskFormDesignerSectionType.Number
    },
    {
        componentType: TaskFormDesignerComponentType.NumberCalculated,
        title: Translations.FormCalculatedDecimalNumber,
        icon: <CalculateIcon />,
        index: 15,
        section: TaskFormDesignerSectionType.Calculated
    },
    {
        componentType: TaskFormDesignerComponentType.IntegerCalculated,
        title: Translations.FormCalculatedIntegerNumber,
        icon: <CalculateIcon />,
        index: 16,
        section: TaskFormDesignerSectionType.Calculated
    },
    {
        componentType: TaskFormDesignerComponentType.LineBreak,
        title: Translations.FormLineBreak,
        icon: <DensityLargeIcon />,
        index: 17,
        section: TaskFormDesignerSectionType.Break
    },
    {
        componentType: TaskFormDesignerComponentType.EmptyArea,
        title: Translations.FormEmptyArea,
        icon: <DensityLargeIcon sx={{ rotate: "90deg" }} />,
        index: 18,
        section: TaskFormDesignerSectionType.Break
    },
    {
        componentType: TaskFormDesignerComponentType.Signature,
        title: Translations.FormSignature,
        icon: <DrawIcon />,
        index: 19,
        section: TaskFormDesignerSectionType.Other
    },
];

const appReportTemplatePickerItems: IFormDesignerComponentPickerItemProps[] = [
    {
        componentType: TaskFormDesignerComponentType.CoverPage,
        title: Translations.FormCoverPage,
        icon: <DescriptionIcon />,
        index: 0,
        section: TaskFormDesignerSectionType.Page
    },
    {
        componentType: TaskFormDesignerComponentType.TitlePage,
        title: Translations.FormTitlePage,
        icon: <DescriptionIcon />,
        index: 1,
        section: TaskFormDesignerSectionType.Page
    },
    {
        componentType: TaskFormDesignerComponentType.PageBreak,
        title: Translations.FormPageBreak,
        icon: <InsertPageBreakIcon />,
        index: 2,
        section: TaskFormDesignerSectionType.Page
    },
    {
        componentType: TaskFormDesignerComponentType.Title1,
        title: Translations.FormTitle1,
        icon: <TitleOutlinedIcon />,
        classes: "eLabel",
        index: 3,
        section: TaskFormDesignerSectionType.Title
    },
    {
        componentType: TaskFormDesignerComponentType.Title2,
        title: Translations.FormTitle2,
        icon: <TitleOutlinedIcon />,
        classes: "lLabel",
        index: 4,
        section: TaskFormDesignerSectionType.Title
    },
    {
        componentType: TaskFormDesignerComponentType.Title3,
        title: Translations.FormTitle3,
        icon: <TitleOutlinedIcon />,
        classes: "mLabel",
        index: 5,
        section: TaskFormDesignerSectionType.Title
    },
    {
        componentType: TaskFormDesignerComponentType.RichText,
        title: Translations.FormNormalText,
        icon: <TitleOutlinedIcon />,
        classes: "fontNormal",
        index: 6,
        section: TaskFormDesignerSectionType.Text
    },
    {
        componentType: TaskFormDesignerComponentType.LineBreak,
        title: Translations.FormLineBreak,
        icon: <DensityLargeIcon />,
        index: 7,
        section: TaskFormDesignerSectionType.Break
    },
    {
        componentType: TaskFormDesignerComponentType.EmptyArea,
        title: Translations.FormEmptyArea,
        icon: <DensityLargeIcon sx={{ rotate: "90deg" }} />,
        index: 8,
        section: TaskFormDesignerSectionType.Break
    },
    {
        componentType: TaskFormDesignerComponentType.DocumentReference,
        title: Translations.FormDocumentReference,
        icon: <SectionIcon type={SectionType.Documents} />,
        index: 9,
        section: TaskFormDesignerSectionType.DataReference
    },
    {
        componentType: TaskFormDesignerComponentType.EventReference,
        title: Translations.FormEventReference,
        icon: <SectionIcon type={SectionType.Events} />,
        index: 10,
        section: TaskFormDesignerSectionType.DataReference
    },
    {
        componentType: TaskFormDesignerComponentType.TaskReference,
        title: Translations.FormTaskReference,
        icon: <SectionIcon type={SectionType.Tasks} />,
        index: 11,
        section: TaskFormDesignerSectionType.DataReference
    },
    {
        componentType: TaskFormDesignerComponentType.RecordReference,
        title: Translations.FormRecordReference,
        icon: <SectionIcon type={SectionType.UserRecords} />,
        index: 12,
        section: TaskFormDesignerSectionType.DataReference
    }
];

export const FormDesignerComponentPicker = () => {
    const renderItems = getRenderItem(taskTypePickerItems);

    return (
        <DialogSectionCard headerTitle={Translations.FormComponents} contentPadding="16px 0 16px 16px!important">
            <Droppable
                isDropDisabled
                droppableId={"componentPickerDroppable"}
                renderClone={renderItems}
            >
                {(provided, _snapshot) => (
                    
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                            position: "relative",
                            height: "calc(100vh - 350px)",
                            overflow: "auto",
                            paddingRight: "16px"
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">{Translations.FormTitles}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.Title).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormTextInputs}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.TextInput).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormDatesAndTimes}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.DateAndTime).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormBooleans}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.Boolean).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormSelection}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.Selection).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormNumbers}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.Number).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormCalculated}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.Calculated).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{`${Translations.FormLineBreak} / ${Translations.FormEmptyArea}`}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.Break).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormOtherInputs}</Typography>
                        <List>
                            {taskTypePickerItems.filter(item => item.section === TaskFormDesignerSectionType.Other).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
        </DialogSectionCard>
    );
};

interface IAppReportDesignerComponentPickerProps {
    companyId: string;
}

export const AppReportDesignerComponentPicker = ({ companyId }: IAppReportDesignerComponentPickerProps) => {
    const fileteredItems = companyId
        ? appReportTemplatePickerItems
        : appReportTemplatePickerItems.filter(item =>
            item.componentType !== TaskFormDesignerComponentType.DocumentReference &&
            item.componentType !== TaskFormDesignerComponentType.EventReference &&
            item.componentType !== TaskFormDesignerComponentType.TaskReference &&
            item.componentType !== TaskFormDesignerComponentType.RecordReference);

    const renderItems = getRenderItem(fileteredItems);

    return (
        <DialogSectionCard headerTitle={Translations.FormComponents} contentPadding="16px 0 16px 16px!important">
            <Droppable
                isDropDisabled
                droppableId={"componentPickerDroppable"}
                renderClone={renderItems}
            >
                {(provided, _snapshot) => (
                    
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                            position: "relative",
                            height: "calc(100vh - 350px)",
                            overflow: "auto",
                            paddingRight: "16px"
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">{Translations.FormPages}</Typography>
                        <List>
                            {fileteredItems.filter(item => item.section === TaskFormDesignerSectionType.Page).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormTitles}</Typography>
                        <List>
                            {fileteredItems.filter(item => item.section === TaskFormDesignerSectionType.Title).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{Translations.FormTexts}</Typography>
                        <List>
                            {fileteredItems.filter(item => item.section === TaskFormDesignerSectionType.Text).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        <Typography variant="caption" color="text.secondary">{`${Translations.FormLineBreak} / ${Translations.FormEmptyArea}`}</Typography>
                        <List>
                            {fileteredItems.filter(item => item.section === TaskFormDesignerSectionType.Break).map((item) => (
                                <FormDesignerComponentPickerItem key={item.index} {...item} />
                            ))}
                        </List>
                        {!!companyId &&
                            <>
                                <Typography variant="caption" color="text.secondary">{Translations.FormDataReference}</Typography>
                                <List>
                                    {fileteredItems.filter(item => item.section === TaskFormDesignerSectionType.DataReference).map((item) => (
                                        <FormDesignerComponentPickerItem key={item.index} {...item} />
                                    ))}
                                </List>
                            </>
                        }
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
        </DialogSectionCard>
    );
};
